import React, { useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/browser';
import {
    listAllHelpSupportDocs,
    updateHelpSupportDoc,
    deleteHelpSupportDoc as deleteHelpSupportDocMutation,
} from '../../graphql/resourceLibraryQueries';
import type { HelpSupportDoc } from '../../types/resourceLibraryTypes';
import { AssignmentModal } from './AssignmentModal';
import { AddEditDocModal } from './AddEditDocModal';
import { DeleteDocModal } from './DeleteDocModal';
import { Btn, BtnIconOnly, Card, IconPencil, IconPlus, IconTrash, TEIQspinner } from '../ui';
import '../../css/ResourceLibraryContent.css';

const VISIBILITY_LABELS: Record<string, string> = {
    all: 'All customers',
    safety: 'Safety module only',
    pm: 'PM module only',
    tires: 'Tires module only',
    'cost-tracking': 'Cost Tracking module only',
};

export const HelpSupportDocsTab: React.FC = () => {

    const [docs, setDocs] = useState<HelpSupportDoc[]>([]);
    const [loading, setLoading] = useState(true);

    // Modal state
    const [addEditModalOpen, setAddEditModalOpen] = useState(false);
    const [editingDoc, setEditingDoc] = useState<HelpSupportDoc | null>(null);
    const [deleteModalDoc, setDeleteModalDoc] = useState<HelpSupportDoc | null>(null);
    const [assignModalDoc, setAssignModalDoc] = useState<HelpSupportDoc | null>(null);

    const fetchDocs = useCallback(async () => {
        setLoading(true);
        try {
            const allDocs = await listAllHelpSupportDocs() as HelpSupportDoc[];
            setDocs(allDocs);
        } catch (err) {
            console.error('Error fetching help support docs:', err);
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDocs();
    }, [fetchDocs]);

    const handleAddClick = () => {
        setEditingDoc(null);
        setAddEditModalOpen(true);
    };

    const handleEditClick = (doc: HelpSupportDoc) => {
        setEditingDoc(doc);
        setAddEditModalOpen(true);
    };

    const handleAddEditClose = () => {
        setAddEditModalOpen(false);
        setEditingDoc(null);
    };

    const handleAddEditSave = () => {
        setAddEditModalOpen(false);
        setEditingDoc(null);
        fetchDocs();
    };

    const handleDeleteClick = (doc: HelpSupportDoc) => {
        setDeleteModalDoc(doc);
    };

    const handleDeleteClose = () => {
        setDeleteModalDoc(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModalDoc) return;
        try {
            await deleteHelpSupportDocMutation(deleteModalDoc.id);
            setDeleteModalDoc(null);
            fetchDocs();
        } catch (err) {
            console.error('Error deleting doc:', err);
            Sentry.captureException(err);
            throw err;
        }
    };

    const handleAssignClick = (doc: HelpSupportDoc) => {
        setAssignModalDoc(doc);
    };

    const handleAssignSave = async (companyIds: string[]) => {
        if (!assignModalDoc) return;
        await updateHelpSupportDoc({
            id: assignModalDoc.id,
            sharedWithCompanyIds: companyIds,
        });
        fetchDocs();
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    if (loading) {
        return (
            <div className="zrl-loading">
                <TEIQspinner label="Loading help and support docs" />
            </div>
        );
    }

    return (
        <>
            {/* Section Header */}
            <div className="zrl-section-header">
                <h2 className="zrl-section-title">
                    Help &amp; Support Docs
                    <span className="zrl-count-badge">{docs.length}</span>
                </h2>
                <Btn
                    variant="secondary-outlined"
                    buttonSize="sm"
                    buttonIcon={<IconPlus />}
                    buttonText="Add Doc"
                    buttonOnClick={handleAddClick}
                />
            </div>

            {/* Table */}
            {docs.length === 0 ? (
                <div className="zrl-empty">
                    No help docs yet. Click "+ Add Doc" to get started.
                </div>
            ) : (
                <Card variant="card-lg" cardClasses="card-pad-0">
                    <div className="zrl-table-wrapper">
                        <table className="zrl-table">
                            <thead>
                                <tr>
                                    <th>Document Name</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Visibility</th>
                                    <th>Last Updated</th>
                                    <th>Assign to Companies</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {docs.map((doc) => {
                                    const isDraft = doc.status === 'DRAFT';

                                    return (
                                        <tr key={doc.id}>
                                            <td>
                                                <div className="zrl-table-name">{doc.title}</div>
                                                {doc.description && (
                                                    <div className="zrl-table-desc">{doc.description}</div>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`zrl-badge ${doc.docType === 'PDF' ? 'zrl-badge-pdf' : 'zrl-badge-video'}`}>
                                                    {doc.docType}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`zrl-badge ${isDraft ? 'zrl-badge-draft' : 'zrl-badge-published'}`}>
                                                    {isDraft ? 'Draft' : 'Published'}
                                                </span>
                                            </td>
                                            <td>
                                                {isDraft ? (
                                                    <span className="zrl-muted">Not yet published</span>
                                                ) : (
                                                    VISIBILITY_LABELS[doc.visibility] || doc.visibility
                                                )}
                                            </td>
                                            <td>{formatDate(doc.updatedAt)}</td>
                                            <td>
                                                <Btn
                                                    variant="secondary-outlined"
                                                    buttonSize="sm"
                                                    buttonText="Manage Assignment"
                                                    buttonDisabled={isDraft}
                                                    buttonOnClick={() => handleAssignClick(doc)}
                                                />
                                            </td>
                                            <td>
                                                <div className="zrl-actions">
                                                    <BtnIconOnly
                                                        size="sm"
                                                        icon={<IconPencil />}
                                                        ariaLabel={`Edit ${doc.title}`}
                                                        onClick={() => handleEditClick(doc)}
                                                    />
                                                    <BtnIconOnly
                                                        size="sm"
                                                        variant="danger"
                                                        icon={<IconTrash />}
                                                        ariaLabel={`Delete ${doc.title}`}
                                                        onClick={() => handleDeleteClick(doc)}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Add/Edit Doc Modal */}
            <AddEditDocModal
                isOpen={addEditModalOpen}
                onClose={handleAddEditClose}
                onSave={handleAddEditSave}
                editingDoc={editingDoc}
            />

            {/* Delete Doc Modal */}
            {deleteModalDoc && (
                <DeleteDocModal
                    isOpen={!!deleteModalDoc}
                    onClose={handleDeleteClose}
                    onDelete={handleDeleteConfirm}
                    docTitle={deleteModalDoc.title}
                    docType={deleteModalDoc.docType}
                    docStatus={deleteModalDoc.status}
                    assignmentCount={
                        deleteModalDoc.sharedWithCompanyIds
                            ? deleteModalDoc.sharedWithCompanyIds.filter(Boolean).length
                            : 0
                    }
                />
            )}

            {/* Assignment Modal */}
            {assignModalDoc && (
                <AssignmentModal
                    isOpen={!!assignModalDoc}
                    onClose={() => { setAssignModalDoc(null); }}
                    resourceName={assignModalDoc.title}
                    resourceType="doc"
                    currentCompanyIds={
                        (assignModalDoc.sharedWithCompanyIds?.filter(Boolean) as string[]) || []
                    }
                    onSave={handleAssignSave}
                />
            )}
        </>
    );
};
